import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { TestPlan, Feature, Component, Session, Platform } from '../../types';
import { apiRequest } from '../../utils/api';
import TestPlanDetails from './TestPlanDetails';
import CreateSessionModal from '../sessions/CreateSessionModal';

const TestPlanDetailPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [testPlan, setTestPlan] = useState<TestPlan | null>(null);
  const [features, setFeatures] = useState<Feature[]>([]);
  const [components, setComponents] = useState<Component[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateSessionModal, setShowCreateSessionModal] = useState(false);
  const [sessionInitialData, setSessionInitialData] = useState<{
    productId?: string;
    features?: string[];
    components?: string[];
    platforms?: Platform[];
    referenceUrl?: string;
    testPlanUrl?: string;
    milestone?: string;
  } | undefined>(undefined);

  useEffect(() => {
    if (!id) return;

    const fetchTestPlan = async () => {
      try {
        const testPlans = await apiRequest<TestPlan[]>('/api/testplans', {}, { useAuth: true });
        const foundTestPlan = testPlans.find((s: TestPlan) => s.id === id);
        setTestPlan(foundTestPlan || null);

        // Fetch related data
        const [featuresData, componentsData] = await Promise.all([
          apiRequest<Feature[]>('/api/features', {}, { useAuth: true }),
          apiRequest<Component[]>('/api/components', {}, { useAuth: true })
        ]);

        setFeatures(featuresData || []);
        setComponents(componentsData || []);
      } catch (error) {
        console.error('Error fetching test plan:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchTestPlan();
  }, [id]);

  const handleClose = () => {
    navigate('/testplan');
  };

  const handleEdit = (testPlanToEdit: TestPlan) => {
    navigate('/testplan', { state: { editTestPlanId: testPlanToEdit.id } });
  };

  const handleComplete = async (scopeId: string) => {
    try {
      await apiRequest(`/api/testplans/${scopeId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'completed' }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });
      
      // Refresh test plan
      const testPlans = await apiRequest<TestPlan[]>('/api/testplans', {}, { useAuth: true });
      const updatedTestPlan = testPlans.find((s: TestPlan) => s.id === scopeId);
      if (updatedTestPlan) {
        setTestPlan(updatedTestPlan);
      }
    } catch (error: any) {
      console.error('Error completing test plan:', error);
      throw error; // Re-throw so the modal doesn't close on error
    }
  };

  const handleReopen = async (scopeId: string) => {
    try {
      await apiRequest(`/api/testplans/${scopeId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: 'active' }),
        headers: { 'Content-Type': 'application/json' }
      }, { useAuth: true });
      
      // Refresh test plan
      const testPlans = await apiRequest<TestPlan[]>('/api/testplans', {}, { useAuth: true });
      const updatedTestPlan = testPlans.find((s: TestPlan) => s.id === scopeId);
      if (updatedTestPlan) {
        setTestPlan(updatedTestPlan);
      }
    } catch (error: any) {
      console.error('Error reopening test plan:', error);
      throw error; // Re-throw so the modal doesn't close on error
    }
  };

  const handleDelete = async (scopeId: string) => {
    try {
      await apiRequest(`/api/testplans/${scopeId}`, {
        method: 'DELETE'
      }, { useAuth: true });
      navigate('/testplan');
    } catch (error: any) {
      console.error('Error deleting test plan:', error);
      throw error; // Re-throw so the modal doesn't close on error
    }
  };

  const handleRunSession = async (testPlanToRun: TestPlan) => {
    if (!testPlanToRun.components || testPlanToRun.components.length === 0) {
      return;
    }

    // Get unique feature IDs from components
    const componentFeatures = new Set<string>();
    testPlanToRun.components.forEach(componentId => {
      const component = components.find(c => c.id === componentId);
      if (component) {
        componentFeatures.add(component.featureId);
      }
    });

    // Use the test plan details URL as the reference URL
    const testPlanUrl = `${window.location.origin}/testplan/${testPlanToRun.id}`;

    // Set initial data and open the create session modal
    setSessionInitialData({
      productId: testPlanToRun.productId,
      features: Array.from(componentFeatures),
      components: testPlanToRun.components,
      platforms: testPlanToRun.testingPlan || [],
      referenceUrl: testPlanUrl,
      milestone: testPlanToRun.milestone
    });
    setShowCreateSessionModal(true);
  };

  if (loading) {
    return <div className="form-container">Loading...</div>;
  }

  if (!testPlan) {
    return (
      <div className="form-container">
        <div className="card">
          <p>Test plan not found.</p>
          <button className="btn btn-secondary" onClick={() => navigate('/testplan')}>
            Back to Test Plans
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <TestPlanDetails
        scope={testPlan}
        onClose={handleClose}
        features={features}
        components={components}
        onEdit={handleEdit}
        onComplete={handleComplete}
        onReopen={handleReopen}
        onDelete={handleDelete}
        onRunSession={handleRunSession}
      />

      {/* Create Session Modal */}
      <CreateSessionModal
        isOpen={showCreateSessionModal}
        onClose={() => {
          setShowCreateSessionModal(false);
          setSessionInitialData(undefined);
        }}
        initialData={sessionInitialData}
        onSessionCreated={async (session: Session) => {
          setShowCreateSessionModal(false);
          setSessionInitialData(undefined);
          // Only navigate to session runner if session was started (status: 'inprogress')
          // If status is 'todo', it means "Save for Later" was clicked, so don't navigate
          if (session.status === 'inprogress') {
            navigate(`/session/${session.id}`);
          }
        }}
      />
    </>
  );
};

export default TestPlanDetailPage;

